import { memo, type ComponentProps } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import 'katex/dist/katex.min.css'
import { cn } from '@/lib/utils'
import { ExternalLink, FileText, Database, BookOpen } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useConversationId, type ConversationLinkState } from './conversationContext'

// Shared prose classes for consistent markdown rendering
const PROSE_CLASSES = cn(
  "prose prose-sm max-w-none",
  "prose-p:my-1",
  "prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5",
  "prose-headings:my-2 prose-headings:font-semibold",
  "prose-code:bg-neutral-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm",
  "prose-pre:bg-neutral-100 prose-pre:p-3",
  "prose-a:text-primary prose-a:no-underline hover:prose-a:underline"
)

/**
 * Custom link renderer for medical reference links.
 * - Opens in new tab
 * - Shows appropriate icon based on source type
 * - Special styling for trusted medical sources
 */
function MedicalLink({ href, children, ...props }: ComponentProps<'a'>) {
  // An agent's link to a screen of this app — a protocol run it offers
  // (`/home/a/protocols/runs/new?…`, which starts the run) — is a router push,
  // so the nurse stays in the shell, never a new tab.
  // A path inside this app has one leading slash; "//host" is
  // protocol-relative, another origin. Clicked inside a conversation, the
  // link carries that conversation as navigation state, so a run it starts
  // hangs under it; the model writes the href, never the state.
  const conversationId = useConversationId()
  if (href !== undefined && href.startsWith('/') && !href.startsWith('//')) {
    const state: ConversationLinkState | undefined = conversationId ? { parent: conversationId } : undefined
    return (
      <Link to={href} state={state} className="font-medium text-primary no-underline hover:underline" data-testid="chat-in-app-link">
        {children}
      </Link>
    )
  }
  const isSwissmedicinfo = href?.includes('swissmedicinfo.ch')
  const isSAI = href?.includes('sai.refdata.ch')
  const isPubMed = href?.includes('pubmed.ncbi.nlm.nih.gov') || href?.includes('ncbi.nlm.nih.gov/pubmed')
  const isTrustedMedical = isSwissmedicinfo || isSAI || isPubMed

  // Choose icon based on source type
  // FileText = Fachinformation, Database = SAI, BookOpen = PubMed, ExternalLink = other
  const Icon = isSwissmedicinfo ? FileText : isSAI ? Database : isPubMed ? BookOpen : ExternalLink

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "inline-flex items-center gap-1",
        "text-primary no-underline hover:underline",
        isTrustedMedical && "font-medium"
      )}
      {...props}
    >
      {children}
      <Icon className="w-3 h-3 inline-block flex-shrink-0" />
    </a>
  )
}

interface MarkdownContentProps {
  content: string
  className?: string
  /** If true, applies font-medium for summary styling */
  isSummary?: boolean
}

// Normalize markdown: strip leaked XML tags, convert bullets, clean SNOMED codes
function normalizeMarkdown(text: string): string {
  let normalized = text;
  // Strip XML tags that leaked through backend parsing
  normalized = normalized.replace(/<\/?(?:summary|praegnant|background|hintergrund)>/gi, '');
  // Defensive [SOURCE] strip: chat.yaml no longer mandates the tag form
  // (PR #1039) and the backend post-processor no longer touches them,
  // but the model may still emit one occasionally from training bias.
  // Strip — don't transform — so we never reintroduce the inline
  // "(Source: …)" parenthetical clutter that PR #1039 removed.
  normalized = normalized.replace(/\s?\[SOURCE\][\s\S]*?\[\/SOURCE\]\s?/gi, ' ');
  // Strip SNOMED codes
  normalized = normalized.replace(/SNOMED-CT:\s*\d+\s*/g, '');
  // Convert Unicode bullets to markdown
  normalized = normalized.replace(/^• /gm, '- ');
  return normalized;
}

export const MarkdownContent = memo(function MarkdownContent({
  content,
  className,
  isSummary = false,
}: MarkdownContentProps) {
  return (
    <div
      className={cn(
        "text-md leading-relaxed",
        PROSE_CLASSES,
        isSummary && "font-medium",
        className
      )}
    >
      <ReactMarkdown
        // remarkMath parses $…$ / $$…$$; rehypeKatex renders it (KaTeX).
        // Clinical answers routinely contain SpO₂, PaO₂, ≥65 mmHg, 37.7 °C as
        // LaTeX from the model — without these they rendered as raw `$…$`.
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          // Custom link renderer - opens in new tab with icon
          a: MedicalLink,
        }}
      >
        {normalizeMarkdown(content)}
      </ReactMarkdown>
    </div>
  )
})
